import { useState } from "react";
import { Loader2, ScanLine, Camera, X, AlertTriangle, CheckCircle2, ArrowRightLeft } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { api, notifyErr, errMsg } from "@/lib/api";

const SECTIONS = { carta_identita: ["Carta d'identità", true], codice_fiscale: ["Codice fiscale / Tessera sanitaria", false], patente: ["Patente di guida", true] };
const FL = { last_name: "Cognome", first_name: "Nome", sex: "Sesso", birth_date: "Data di nascita", birth_place: "Luogo di nascita", tax_code: "Codice fiscale",
  nationality: "Cittadinanza", address: "Indirizzo", city: "Città", doc_number: "Numero documento", issue_date: "Rilascio", expiry_date: "Scadenza",
  issuing_authority: "Autorità", issue_place: "Luogo rilascio", license_categories: "Categorie" };

function toCustomer(secs) {
  const out = {};
  const common = ["last_name", "first_name", "birth_date", "birth_place", "address", "city", "nationality", "tax_code"];
  Object.values(secs).forEach((f) => common.forEach((k) => { if (f[k] && !out[k]) out[k] = f[k]; }));
  const ci = secs.carta_identita, pa = secs.patente;
  if (ci) Object.assign(out, { doc_type: "carta_identita", doc_number: ci.doc_number, doc_expiry: ci.expiry_date, doc_issue_place: ci.issue_place || ci.issuing_authority });
  if (pa) Object.assign(out, { license_number: pa.doc_number, license_expiry: pa.expiry_date, license_issue_place: pa.issue_place || pa.issuing_authority });
  return Object.fromEntries(Object.entries(out).filter(([, v]) => v));
}

function FilePick({ label: lbl, file, onFile, testid }) {
  return (
    <div className="flex items-center gap-2">
      <label className="flex-1 flex items-center gap-2 h-10 px-3 rounded-md border border-zinc-700 bg-zinc-900 cursor-pointer text-sm truncate hover:border-brand" data-testid={testid}>
        <input type="file" accept="image/jpeg,image/png,application/pdf" className="hidden" data-testid={`${testid}-input`} onChange={(e) => { onFile(e.target.files[0] || null); e.target.value = ""; }} />
        <ScanLine size={15} className="shrink-0" /><span className="truncate">{file ? file.name : lbl}</span>
      </label>
      <label className="h-10 w-10 grid place-items-center rounded-md border border-zinc-700 cursor-pointer" title="Scatta con fotocamera">
        <input type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { onFile(e.target.files[0] || null); e.target.value = ""; }} /><Camera size={15} />
      </label>
      {file && <button onClick={() => onFile(null)} className="text-zinc-500"><X size={15} /></button>}
    </div>
  );
}

export default function AiDocuments({ customer, onSaved }) {
  const [files, setFiles] = useState({});
  const [results, setResults] = useState({});
  const [busy, setBusy] = useState(null);
  const [review, setReview] = useState(null);

  const setF = (sec, side, f) => { setFiles((s) => ({ ...s, [sec]: { ...s[sec], [side]: f } })); setResults((r) => ({ ...r, [sec]: undefined })); };
  const clear = (sec) => { setFiles((s) => ({ ...s, [sec]: {} })); setResults((r) => ({ ...r, [sec]: undefined })); };

  const analyze = async (sec, fs = files[sec]) => {
    if (!fs?.front) { toast.error("Carica almeno il fronte del documento"); return; }
    const fd = new FormData(); fd.append("section", sec); fd.append("front", fs.front); if (fs.back) fd.append("back", fs.back);
    setBusy(sec);
    try { const r = await api.post("/ai/analyze", fd); setResults((x) => ({ ...x, [sec]: r.data })); }
    catch (e) { setResults((x) => ({ ...x, [sec]: { outcome: "error", message: errMsg(e) } })); } finally { setBusy(null); }
  };
  const moveTo = (from, to) => {
    const fs = files[from];
    setFiles((s) => ({ ...s, [to]: fs, [from]: {} }));
    setResults((r) => ({ ...r, [from]: undefined }));
    analyze(to, fs);
  };
  const editField = (sec, k, v) => setResults((r) => ({ ...r, [sec]: { ...r[sec], fields: { ...r[sec].fields, [k]: v } } }));

  const openReview = async () => {
    const secs = Object.fromEntries(Object.entries(results).filter(([, r]) => r?.outcome === "ok").map(([k, r]) => [k, r.fields]));
    if (!Object.keys(secs).length) { toast.error("Nessun documento valido da confermare"); return; }
    try { const r = await api.post("/ai/compare", { sections: secs }); setReview({ secs, cmp: r.data, data: toCustomer(secs) }); } catch (e) { notifyErr(e); }
  };
  const confirm = async () => {
    try {
      for (const sec of Object.keys(review.secs)) {
        for (const side of ["front", "back"]) {
          const f = files[sec]?.[side];
          if (!f) continue;
          const fd = new FormData(); fd.append("file", f); fd.append("category", "cliente_documento"); fd.append("entity_type", "customer");
          fd.append("entity_id", customer.id); fd.append("side", side === "front" ? "fronte" : "retro"); fd.append("notes", SECTIONS[sec][0]);
          await api.post("/documents", fd);
        }
      }
      await api.put(`/customers/${customer.id}`, review.data);
      toast.success("Dati e documenti salvati nella scheda cliente");
      setReview(null); setFiles({}); setResults({}); onSaved && onSaved();
    } catch (e) { notifyErr(e); }
  };

  return (
    <div className="space-y-4" data-testid="ai-documents">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {Object.entries(SECTIONS).map(([sec, [title, twoSides]]) => {
          const r = results[sec];
          return (
            <div key={sec} className="border border-zinc-800 rounded-xl p-4 bg-zinc-900/30 space-y-3" data-testid={`ai-section-${sec}`}>
              <div className="font-display font-semibold">{title}</div>
              <FilePick label="Fronte (JPG, PNG, PDF)" file={files[sec]?.front} onFile={(f) => setF(sec, "front", f)} testid={`ai-${sec}-front`} />
              {twoSides && <FilePick label="Retro (opzionale)" file={files[sec]?.back} onFile={(f) => setF(sec, "back", f)} testid={`ai-${sec}-back`} />}
              <Button className="w-full btn-brand" disabled={busy === sec} onClick={() => analyze(sec)} data-testid={`ai-${sec}-analyze`}>
                {busy === sec ? <><Loader2 className="animate-spin mr-1" size={14} />Verifica in corso…</> : "Verifica e leggi con AI"}</Button>
              {r && r.outcome !== "ok" && (
                <div className="text-sm rounded-md border border-red-500/30 bg-red-500/10 p-3 space-y-2" data-testid={`ai-${sec}-message`}>
                  <div className="flex gap-2"><AlertTriangle size={16} className="text-red-300 shrink-0 mt-0.5" />{r.message}</div>
                  <div className="flex flex-wrap gap-2">
                    {r.outcome === "wrong_section" && <Button size="sm" variant="outline" onClick={() => moveTo(sec, r.detected_category)} data-testid={`ai-${sec}-move`}><ArrowRightLeft size={14} className="mr-1" />Sposta in {SECTIONS[r.detected_category][0]}</Button>}
                    <Button size="sm" variant="outline" onClick={() => clear(sec)} data-testid={`ai-${sec}-retry`}>{r.outcome === "wrong_section" ? "Sostituisci" : "Carica un altro documento"}</Button>
                    {r.outcome === "invalid" && <Button size="sm" variant="ghost" onClick={() => clear(sec)} data-testid={`ai-${sec}-cancel`}>Annulla caricamento</Button>}
                  </div>
                </div>
              )}
              {r?.outcome === "ok" && (
                <div className="space-y-2" data-testid={`ai-${sec}-fields`}>
                  <div className="text-xs text-emerald-300 flex gap-1 items-center"><CheckCircle2 size={14} />{r.message}</div>
                  {r.expired && <div className="text-xs text-red-300">Documento scaduto</div>}
                  {r.tax_code_check && <div className={`text-xs ${r.tax_code_check.valid ? "text-emerald-300" : "text-red-300"}`}>{r.tax_code_check.message}</div>}
                  {Object.entries(r.fields).map(([k, v]) => (
                    <div key={k}><div className="text-[10px] uppercase text-zinc-500">{FL[k] || k}</div><Input className="h-8" value={v || ""} onChange={(e) => editField(sec, k, e.target.value)} /></div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
      <div className="flex justify-end"><Button className="btn-brand" onClick={openReview} data-testid="ai-review-btn">Anteprima e conferma</Button></div>
      <Dialog open={!!review} onOpenChange={(o) => !o && setReview(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="ai-review-dialog">
          <DialogHeader><DialogTitle>Verifica i dati prima del salvataggio</DialogTitle><DialogDescription>I valori sono stati letti dai documenti. Correggili se necessario: verranno salvati solo dopo la conferma.</DialogDescription></DialogHeader>
          {review && <>
            {(review.cmp.issues.length > 0 || review.cmp.missing.length > 0) && (
              <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-3 text-sm space-y-1" data-testid="ai-review-issues">
                {review.cmp.issues.map((i) => <div key={i}>⚠ {i}</div>)}{review.cmp.missing.map((i) => <div key={i} className="text-zinc-300">• {i}</div>)}
              </div>
            )}
            {review.cmp.tax_code_check && <div className={`text-sm ${review.cmp.tax_code_check.valid ? "text-emerald-300" : "text-red-300"}`}>{review.cmp.tax_code_check.message}</div>}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {Object.entries(review.data).map(([k, v]) => (
                <div key={k}><div className="text-[11px] text-zinc-500">{k}</div><Input value={v || ""} onChange={(e) => setReview((r) => ({ ...r, data: { ...r.data, [k]: e.target.value } }))} data-testid={`ai-review-${k}`} /></div>
              ))}
            </div>
            <DialogFooter><Button variant="outline" onClick={() => setReview(null)} data-testid="ai-review-cancel">Annulla</Button><Button className="btn-brand" onClick={confirm} data-testid="ai-review-confirm">Conferma e salva</Button></DialogFooter>
          </>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
